import React, { useState } from 'react'

import type { SimilarityConfig } from '../../services/analyticsService'

interface ConfigurationModalProps {
  isOpen: boolean
  onClose: () => void
  onSubmit: (config: Partial<SimilarityConfig>) => void
  currentConfig?: SimilarityConfig
}

export function ConfigurationModal({
  isOpen,
  onClose,
  onSubmit,
  currentConfig,
}: ConfigurationModalProps) {
  const [config, setConfig] = useState<Partial<SimilarityConfig>>({
    exactThreshold: currentConfig?.exactThreshold || 0.8,
    phoneticThreshold: currentConfig?.phoneticThreshold || 0.9,
    stemThreshold: currentConfig?.stemThreshold || 0.95,
    semanticThreshold: currentConfig?.semanticThreshold || 0.8,
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    onSubmit(config)
    onClose()
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white p-6 rounded-lg max-w-md w-full mx-4">
        <h3 className="text-lg font-semibold mb-4">
          Configure Similarity Thresholds
        </h3>
        <form onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div>
              <label
                htmlFor="exactThreshold"
                className="block text-sm font-medium mb-1"
              >
                Exact/Near Match Threshold (0-1)
              </label>
              <input
                id="exactThreshold"
                type="number"
                min="0"
                max="1"
                step="0.05"
                value={config.exactThreshold}
                onChange={(e) =>
                  setConfig({
                    ...config,
                    exactThreshold: parseFloat(e.target.value),
                  })
                }
                className="w-full p-2 border rounded"
              />
              <p className="text-xs text-gray-600 mt-1">
                Minimum similarity for Levenshtein/Jaro-Winkler matches
              </p>
            </div>

            <div>
              <label
                htmlFor="phoneticThreshold"
                className="block text-sm font-medium mb-1"
              >
                Phonetic Threshold (0-1)
              </label>
              <input
                id="phoneticThreshold"
                type="number"
                min="0"
                max="1"
                step="0.05"
                value={config.phoneticThreshold}
                onChange={(e) =>
                  setConfig({
                    ...config,
                    phoneticThreshold: parseFloat(e.target.value),
                  })
                }
                className="w-full p-2 border rounded"
              />
              <p className="text-xs text-gray-600 mt-1">
                Minimum similarity for sound-alike matches (Metaphone)
              </p>
            </div>

            <div>
              <label
                htmlFor="stemThreshold"
                className="block text-sm font-medium mb-1"
              >
                Stem Threshold (0-1)
              </label>
              <input
                id="stemThreshold"
                type="number"
                min="0"
                max="1"
                step="0.05"
                value={config.stemThreshold}
                onChange={(e) =>
                  setConfig({
                    ...config,
                    stemThreshold: parseFloat(e.target.value),
                  })
                }
                className="w-full p-2 border rounded"
              />
              <p className="text-xs text-gray-600 mt-1">
                Minimum similarity for word stem matches (Porter Stemmer)
              </p>
            </div>
          </div>

          <div className="flex gap-2 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 rounded hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Apply
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
